export type AzureRelayRegion =
  | "europe"
  | "america_south"
  | "america_north"
  | "asia"
  | "middle_east"
  | "africa";

export const AZURE_RELAY_LOCATION_REGION_MAP: Record<
  AzureRelayRegion,
  string[]
> = {
  europe: [
    "norwayeast",
    "northeurope",
    "westeurope",
    "swedencentral",
    "francecentral",
    "germanywestcentral",
    "polandcentral",
    "spaincentral",
  ],
  america_south: ["brazilsouth", "mexicocentral"],
  america_north: [
    "canadacentral",
    "canadaeast",
    "centralus",
    "eastus",
    "eastus2",
    "eastus2euap",
    "northcentralus",
    "southcentralus",
    "westcentralus",
    "westus",
    "westus2",
    "westus3",
    "eastusslv",
    "mexicocentral",
  ],
  asia: [
    "centralindia",
    "eastasia",
    "japaneast",
    "japanwest",
    "jioindiacentral",
    "jioindiawest",
    "koreacentral",
    "koreasouth",
    "southeastasia",
    "southindia",
    "westindia",
    "taiwannorth",
    "taiwannorthwest",
    "malaysiasouth",
    "australiacentral",
    "australiacentral2",
    "australiaeast",
    "australiasoutheast",
  ],
  middle_east: ["qatarcentral", "uaecentral", "uaenorth"],
  africa: ["southafricanorth", "southafricawest"],
};

export const AZURE_RELAY_REGION_OPTIONS: Array<{
  label: string;
  value: AzureRelayRegion;
}> = [
  { label: "Europe", value: "europe" },
  { label: "South America", value: "america_south" },
  { label: "North America", value: "america_north" },
  { label: "Asia", value: "asia" },
  { label: "Middle East", value: "middle_east" },
  { label: "Africa", value: "africa" },
];

const REGION_ORDER: AzureRelayRegion[] = [
  "europe",
  "america_south",
  "america_north",
  "asia",
  "middle_east",
  "africa",
];

/**
 * Per-relay azure_relay_location options: preferred region first, then other
 * geographies in a stable order. Dedupes locations that appear in multiple maps.
 */
export function azureRelayLocationOptions(
  preferredRegion: AzureRelayRegion,
): Array<{ label: string; value: string; group: string }> {
  const seen = new Set<string>();
  const options: Array<{ label: string; value: string; group: string }> = [];

  const appendRegion = (region: AzureRelayRegion) => {
    const groupLabel =
      AZURE_RELAY_REGION_OPTIONS.find((o) => o.value === region)?.label ||
      region;
    for (const location of AZURE_RELAY_LOCATION_REGION_MAP[region]) {
      if (seen.has(location)) continue;
      seen.add(location);
      options.push({
        label: location,
        value: location,
        group: groupLabel,
      });
    }
  };

  appendRegion(preferredRegion);
  for (const region of REGION_ORDER) {
    if (region === preferredRegion) continue;
    appendRegion(region);
  }
  return options;
}

export const BACKEND_TYPE_OPTIONS = [
  { label: "Morpheus", value: "morpheus" },
  { label: "Mythic", value: "mythic" },
  { label: "Cobalt Strike", value: "cobaltstrike" },
  { label: "Teams", value: "teams" },
  { label: "C3", value: "c3" },
];

/** Strip legacy ".profile" suffix; terraform expects ids like "mythic". */
export function normalizeRelayProfile(profile: string | undefined): string {
  const trimmed = (profile || "").trim();
  if (!trimmed) return "";
  return trimmed.endsWith(".profile")
    ? trimmed.slice(0, -".profile".length)
    : trimmed;
}

/** Default relay_profile_type when creating a backend of this type. */
export function defaultRelayProfileForType(type: string): string | undefined {
  switch (type.toLowerCase()) {
    case "cobaltstrike":
      return "cobaltstrike-v1";
    case "mythic":
      return "mythic";
    case "teams":
      return "teams";
    default:
      // morpheus / c3 / relay: no profile
      return undefined;
  }
}

/** Selectable profile options for a backend type; null when the type has none. */
export function relayProfileOptionsForBackendType(
  type: string,
): Array<{ label: string; value: string }> | null {
  switch (type.toLowerCase()) {
    case "cobaltstrike":
      return [
        { label: "cobaltstrike-v1", value: "cobaltstrike-v1" },
        { label: "cobaltstrike", value: "cobaltstrike" },
      ];
    case "mythic":
      return [{ label: "mythic", value: "mythic" }];
    case "teams":
      return [{ label: "teams", value: "teams" }];
    default:
      return null;
  }
}

export function normalizeCodename(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]+/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function normalizeCDNSlotsForPreview(relay: {
  cdn_slots?: Array<{ provider: string; domain_fixed?: string }>;
  cdn_domains_count?: number;
}): Record<string, { provider: string; domain_fixed?: string }> | null {
  const raw = relay.cdn_slots || [];
  const slots =
    raw.length > 0
      ? raw
          .map((slot) => {
            let provider = (slot.provider || "").trim().toLowerCase();
            if (provider === "azure_frontdoor") provider = "frontdoor";
            if (
              provider !== "azure" &&
              provider !== "frontdoor" &&
              provider !== "fastly"
            ) {
              return null;
            }
            const fixed = (slot.domain_fixed || "").trim().toLowerCase();
            return fixed
              ? { provider, domain_fixed: fixed }
              : { provider };
          })
          .filter(
            (slot): slot is { provider: string; domain_fixed?: string } =>
              !!slot,
          )
      : Array.from(
          {
            length: Math.max(0, Math.floor(relay.cdn_domains_count ?? 0)),
          },
          () => ({ provider: "azure" }),
        );
  if (slots.length === 0) return null;
  const out: Record<string, { provider: string; domain_fixed?: string }> = {};
  slots.forEach((slot, index) => {
    out[String(index + 1)] = slot;
  });
  return out;
}

export function buildTerraformPreview(input: {
  clientKey?: string;
  projectCode: string;
  projectCodename: string;
  owner: string;
  createdBy: string;
  azureRgLocation?: string;
  azureRelayRegion: AzureRelayRegion;
  projectTeam: string[];
  tenantProvider: string;
  webhookUrl?: string;
  channelName?: string;
  backends: Array<{ type: string; relay_profile_type?: string }>;
  relays: Array<{
    type: string;
    connected_to_backend: number;
    relay_profile_type?: string;
    domain?: string;
    cdn_slots?: Array<{ provider: string; domain_fixed?: string }>;
    cdn_domains_count?: number;
    azure_relay_location?: string;
    standalone?: boolean;
  }>;
  wireguards: Array<{ azure_relay_location?: string }>;
}): Record<string, unknown> {
  const parsed = Number.parseInt(input.clientKey?.trim() || "1", 10);
  const clientId =
    Number.isFinite(parsed) && parsed >= 1 && parsed <= 99 ? parsed : 1;
  const backends: Record<string, unknown> = {
    "0": { type: "relays" },
  };
  input.backends.forEach((backend, index) => {
    backends[String(index + 1)] = { type: backend.type.toLowerCase() };
  });

  const resolveProfile = (
    relay: (typeof input.relays)[number],
    typeName: string,
  ): string | undefined => {
    const fromRelay = normalizeRelayProfile(relay.relay_profile_type);
    if (fromRelay) return fromRelay;
    if (!relay.standalone && relay.connected_to_backend >= 1) {
      const backend = input.backends[relay.connected_to_backend - 1];
      const fromBackend = normalizeRelayProfile(backend?.relay_profile_type);
      if (fromBackend) return fromBackend;
      return defaultRelayProfileForType(backend?.type || typeName);
    }
    return defaultRelayProfileForType(typeName);
  };

  const relays: Record<string, unknown> = {};
  let relayId = 1;
  input.wireguards.forEach((wg) => {
    const entry: Record<string, unknown> = {
      type: "wireguard",
      connected_to_backend: 0,
    };
    if (wg.azure_relay_location?.trim()) {
      entry.azure_relay_location = wg.azure_relay_location.trim();
    }
    relays[String(relayId++)] = entry;
  });

  input.relays.forEach((relay) => {
    const standalone = !!relay.standalone || relay.type === "relay";
    const entry: Record<string, unknown> = {};
    if (standalone) {
      entry.type = "relay";
      entry.connected_to_backend = 0;
      const profile = resolveProfile(relay, "relay");
      if (profile) entry.relay_profile_type = profile;
      if (relay.domain?.trim()) entry.domain = relay.domain.trim();
    } else {
      const typeName = relay.type.toLowerCase();
      entry.type = typeName;
      entry.connected_to_backend = relay.connected_to_backend;
      const profile = resolveProfile(relay, typeName);
      if (profile) entry.relay_profile_type = profile;
      entry.domain = relay.domain?.trim() || "";
    }
    if (relay.azure_relay_location?.trim()) {
      entry.azure_relay_location = relay.azure_relay_location.trim();
    }
    const slots = normalizeCDNSlotsForPreview(relay);
    if (slots) entry.cdn_slots = slots;
    relays[String(relayId++)] = entry;
  });

  return {
    client_id: clientId,
    shared_state_key: "shared/terraform.tfstate",
    client: {
      project_code: input.projectCode,
      project_codename: normalizeCodename(input.projectCodename),
      owner: input.owner,
      created_by: input.createdBy,
      azure_rg_location: input.azureRgLocation || "westeurope",
      azure_relay_region: input.azureRelayRegion,
      project_team: input.projectTeam
        .map((m) => m.trim().toLowerCase())
        .filter(Boolean),
      tenant_separation: true,
      tenant_provider: input.tenantProvider,
      webhook_url: (input.webhookUrl || "").trim(),
      channel_name: (input.channelName || "").trim(),
      c2_backend: backends,
      relays,
    },
  };
}

const CLIENT_KEY_ORDER = [
  "owner",
  "relays",
  "c2_backend",
  "created_by",
  "webhook_url",
  "channel_name",
  "project_code",
  "project_team",
  "tenant_provider",
  "project_codename",
  "azure_rg_location",
  "tenant_separation",
  "azure_relay_region",
];

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** Normalize legacy values when rendering terraform for copy/paste. */
function normalizeTerraformValue(key: string, value: unknown): unknown {
  if (key === "relay_profile_type" && typeof value === "string") {
    return normalizeRelayProfile(value);
  }
  if (key === "provider" && typeof value === "string") {
    const provider = value.trim().toLowerCase();
    return provider === "azure_frontdoor" ? "frontdoor" : provider;
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeTerraformValue("", item));
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [childKey, childValue] of Object.entries(value)) {
      out[childKey] = normalizeTerraformValue(childKey, childValue);
    }
    return out;
  }
  return value;
}

function orderedObjectKeys(
  value: Record<string, unknown>,
  preferred: string[] = [],
): string[] {
  const keys = Object.keys(value);
  const preferredPresent = preferred.filter((key) => key in value);
  const rest = keys
    .filter((key) => !preferred.includes(key))
    .sort((a, b) => {
      const an = Number(a);
      const bn = Number(b);
      if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
      return a.localeCompare(b);
    });
  return [...preferredPresent, ...rest];
}

function formatJsonishValue(value: unknown, indent: number): string[] {
  const pad = "  ".repeat(indent);
  if (value === null || value === undefined) {
    return [`${pad}null`];
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return [`${pad}${JSON.stringify(value)}`];
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return [`${pad}[]`];
    const lines = [`${pad}[`];
    value.forEach((item, index) => {
      const itemLines = formatJsonishValue(item, indent + 1);
      const isLast = index === value.length - 1;
      // Comma-separate list entries (e.g. project_team).
      if (!isLast && itemLines.length > 0) {
        itemLines[itemLines.length - 1] = `${itemLines[itemLines.length - 1]},`;
      }
      lines.push(...itemLines);
    });
    lines.push(`${pad}]`);
    return lines;
  }
  if (isPlainObject(value)) {
    const keys = orderedObjectKeys(value);
    if (keys.length === 0) return [`${pad}{}`];
    const lines = [`${pad}{`];
    for (const key of keys) {
      const child = value[key];
      if (isPlainObject(child) || Array.isArray(child)) {
        const childLines = formatJsonishValue(child, indent + 1);
        // "key": { / [
        lines.push(`${pad}  ${JSON.stringify(key)}: ${childLines[0].trimStart()}`);
        lines.push(...childLines.slice(1));
      } else {
        lines.push(
          `${pad}  ${JSON.stringify(key)}: ${JSON.stringify(child ?? null)}`,
        );
      }
    }
    lines.push(`${pad}}`);
    return lines;
  }
  return [`${pad}${JSON.stringify(String(value))}`];
}

/**
 * Format stored/preview terraform JSON into the HCL-style document used for
 * copy/paste (top-level client / client_id / shared_state_key assignments).
 */
export function formatTerraformForCopy(doc: unknown): string {
  let parsed: unknown = doc;
  if (typeof doc === "string") {
    try {
      parsed = JSON.parse(doc);
    } catch {
      return doc;
    }
  }
  if (!isPlainObject(parsed)) {
    return String(doc ?? "");
  }

  const normalized = normalizeTerraformValue("", parsed) as Record<
    string,
    unknown
  >;
  const client = isPlainObject(normalized.client) ? normalized.client : {};
  const clientId = normalized.client_id ?? 1;
  const sharedStateKey =
    typeof normalized.shared_state_key === "string" &&
    normalized.shared_state_key.trim()
      ? normalized.shared_state_key
      : "shared/terraform.tfstate";

  const orderedClient: Record<string, unknown> = {};
  for (const key of orderedObjectKeys(client, CLIENT_KEY_ORDER)) {
    orderedClient[key] = client[key];
  }

  const clientLines = formatJsonishValue(orderedClient, 1);
  // formatJsonishValue wraps with { } at indent 1; reuse inner lines for client = { ... }
  const inner = clientLines.slice(1, -1);

  const lines = ["client =  {"];
  lines.push(...inner);
  lines.push("  }");
  lines.push(`  client_id = ${JSON.stringify(clientId)}`);
  lines.push(`  shared_state_key = ${JSON.stringify(sharedStateKey)}`);
  return lines.join("\n");
}
